import { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * usePet — single source of truth for the user's Phoenix Pet.
 * Used by /pet, the floating widget, profile mini, and anywhere else that
 * needs to react to pet state. Polls every 60s so mood/death/age stay fresh.
 */
export function usePet() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const reload = useCallback(async () => {
    if (!user) { setData(null); setLoading(false); return; }
    try {
      const { data } = await axios.get(`${API}/pet`);
      setData(data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [user]);
  useEffect(() => {
    reload();
    const id = setInterval(reload, 60_000);
    return () => clearInterval(id);
  }, [reload]);
  return { data, loading, reload, setData };
}

export const MOOD_COPY = {
  happy:  { face: "😊", label: "Happy",  hint: "Pet is thriving — keep it up!" },
  hungry: { face: "🥺", label: "Hungry", hint: "Hasn't eaten in 24h. XP earn is at 50%." },
  sleepy: { face: "😴", label: "Sleepy", hint: "You haven't logged in for a while." },
  ash:    { face: "💀", label: "Ash",    hint: "Pet has burned. Perform a Rebirth Ritual." },
};

export const TIER_COLORS = {
  egg:              { ring: "border-zinc-700",      glow: "" },
  chick:            { ring: "border-amber-400",    glow: "shadow-[0_0_18px_rgba(251,191,36,0.4)]" },
  fledgling:        { ring: "border-orange-500",   glow: "shadow-[0_0_20px_rgba(249,115,22,0.5)]" },
  phoenix:          { ring: "border-red-500",      glow: "shadow-[0_0_22px_rgba(239,68,68,0.6)]" },
  inferno_phoenix:  { ring: "border-[#D4AF37]",    glow: "shadow-[0_0_30px_rgba(212,175,55,0.7)] animate-pulse" },
};

export function formatRebirthCost(cost) {
  const icons = {
    life_ash: "💀",
    phoenix_feathers: "🪶",
    phoenix_tears: "💧",
    phoenix_crowns: "👑",
    phoenix_hearts: "🫀",
  };
  return Object.entries(cost || {}).map(([k, v]) => `${v} ${icons[k] || ""}`).join(" + ");
}

/**
 * useDailyGreeterToast — fires the daily greeter quote once per session via toast.
 * Mounted high in the app tree (App.js or Layout).
 */
export function useShownDailyQuoteRef() {
  const ref = useRef(null);
  return ref;
}
